/**
 * @file src/components/providers/FormProvider.tsx
 * @description This file defines a React context and provider for managing and providing form data within the application.
 * It includes functions for updating and retrieving form data, ensuring that data is available to all components that require it.
 * @module FormProvider
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

interface FormContextType<T> {
  updateFormData: (key: string, newData: Partial<T>) => void;
  getFormData: (key: string) => T;
}

const FormContext = createContext<FormContextType<any> | undefined>(undefined);

export const useFormData = <T,>(
  key: string,
): { formData: T; updateFormData: (newData: Partial<T>) => void } => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormData must be used within a FormProvider');
  }
  return {
    formData: context.getFormData(key),
    updateFormData: (newData: Partial<T>) =>
      context.updateFormData(key, newData),
  };
};

interface FormProviderProps {
  children: ReactNode;
}

export const FormProvider = ({ children }: FormProviderProps): JSX.Element => {
  const [formStates, setFormStates] = useState<{ [key: string]: any }>({});

  const updateFormData = <T,>(key: string, newData: Partial<T>) => {
    setFormStates((prevStates) => ({
      ...prevStates,
      [key]: { ...prevStates[key], ...newData },
    }));
  };

  const getFormData = <T,>(key: string): T => {
    return formStates[key] || ({} as T);
  };

  return (
    <FormContext.Provider value={{ updateFormData, getFormData }}>
      {children}
    </FormContext.Provider>
  );
};
